import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { ErrorAlert } from '../components';
import type { SettingValue } from '../integrationTypes';
import { fieldErrors, secretFieldProps } from './CredentialFields';
import { useAction } from './IntegrationsContext';
import { humanizeKey, inheritText, settingText } from './integrationLabels';

type Schema = Record<string, any>;
type Level = 'connection' | 'account';

const same = (a: any, b: any) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A property, its `$ref` followed (within the schema's `$defs` / `definitions`). */
const resolve = (root: Schema, prop: Schema | undefined): Schema => {
    if (prop && typeof prop.$ref === 'string' && prop.$ref.startsWith('#/')) {
        const target = prop.$ref.slice(2).split('/').reduce((node: any, key: string) => (node ? node[key] : undefined), root);
        return target ? { ...target, ...prop, $ref: undefined } : prop;
    }
    return prop || {};
};

const typeOf = (prop: Schema): string => {
    let type = prop.type;
    if (Array.isArray(type)) {
        type = type.find((t: string) => t !== 'null');
    }
    if (!type && prop.enum) {
        type = typeof prop.enum[0] === 'number' ? 'number' : 'string';
    }
    if (!type && prop.properties) {
        type = 'object';
    }
    return type || 'string';
};

type Widget = 'secret' | 'enum' | 'boolean' | 'number' | 'list' | 'group' | 'text' | 'readonly';

/** The control a schema property gets (plan §4: switch, select with enum labels, number + unit, text, chip
 *  list, secret "Replace", grouped object). An object without properties is shown, never edited as JSON. */
export const widgetFor = (prop: Schema): Widget => {
    if (prop['x-secret']) {
        return 'secret';
    }
    if (Array.isArray(prop.enum)) {
        return 'enum';
    }
    const type = typeOf(prop);
    if (type === 'boolean') {
        return 'boolean';
    }
    if (type === 'integer' || type === 'number') {
        return 'number';
    }
    if (type === 'array') {
        return 'list';
    }
    if (type === 'object') {
        return prop.properties ? 'group' : 'readonly';
    }
    return 'text';
};

export const labelOf = (key: string, prop: Schema) => prop.title || humanizeKey(key);

interface ControlProps {
    id: string;
    label: string;
    prop: Schema;
    root: Schema;
    value: any;
    disabled: boolean;
    onChange: (value: any) => void;
    onInvalid: (invalid: boolean) => void;
    /** What an empty field means (the default or the connection's value), shown greyed in it. */
    placeholder?: string;
}

/** Typed fields keep their own text: nothing replaces it while the user types. Undo, Reset and a save remount
 *  the control (its `key`), which starts again from the value. */
const NumberInput = ({ id, label, prop, value, disabled, onChange, onInvalid, placeholder }: ControlProps) => {
    const integer = typeOf(prop) === 'integer';
    const [text, setText] = React.useState(value === null || value === undefined ? '' : String(value));
    const parse = (raw: string) => {
        const trimmed = raw.trim();
        if (trimmed === '') {
            return { ok: true, value: null };
        }
        const n = Number(trimmed);
        const ok = Number.isFinite(n) && (!integer || Number.isInteger(n))
            && (prop.minimum === undefined || n >= prop.minimum) && (prop.maximum === undefined || n <= prop.maximum);
        return { ok, value: n };
    };
    const state = parse(text);
    const range = prop.minimum !== undefined || prop.maximum !== undefined
        ? ` ${[prop.minimum !== undefined && `from ${prop.minimum}`, prop.maximum !== undefined && `to ${prop.maximum}`].filter(Boolean).join(' ')}`
        : '';
    return (
        <TextField
            id={id}
            size="small"
            value={text}
            disabled={disabled}
            placeholder={placeholder}
            error={!state.ok}
            helperText={state.ok ? undefined : `${integer ? 'A whole number' : 'A number'}${range}`}
            onChange={(ev) => {
                setText(ev.target.value);
                const next = parse(ev.target.value);
                onInvalid(!next.ok);
                if (next.ok) {
                    onChange(next.value);
                }
            }}
            inputProps={{ inputMode: integer ? 'numeric' : 'decimal', 'aria-label': label, style: { fontVariantNumeric: 'tabular-nums' } }}
            InputProps={prop['x-unit'] ? { endAdornment: <InputAdornment position="end">{prop['x-unit']}</InputAdornment> } : undefined}
            sx={{ width: { xs: '100%', md: 220 } }}
        />
    );
};

const ListInput = ({ id, label, prop, value, disabled, onChange }: ControlProps) => {
    const items = resolveItems(prop);
    const options: string[] = Array.isArray(items.enum) ? items.enum.map(String) : [];
    const numeric = ['integer', 'number'].includes(typeOf(items));
    const list: any[] = Array.isArray(value) ? value : [];
    return (
        <Autocomplete
            id={id}
            multiple
            freeSolo={options.length === 0}
            options={options}
            value={list.map(String)}
            disabled={disabled}
            size="small"
            fullWidth
            onChange={(_ev, next) => {
                const cleaned = (next as string[]).map((s) => s.trim()).filter(Boolean);
                const unique = cleaned.filter((s, i) => cleaned.indexOf(s) === i);
                onChange(numeric ? unique.map(Number).filter((n) => Number.isFinite(n)) : unique);
            }}
            renderTags={(tags, getTagProps) => tags.map((tag, index) => <Chip size="small" label={tag} {...getTagProps({ index })} key={tag} />)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={list.length ? undefined : options.length ? 'Choose…' : 'Type and press Enter'}
                    inputProps={{ ...params.inputProps, 'aria-label': label }}
                />
            )}
        />
    );
};

const resolveItems = (prop: Schema): Schema => (prop.items && !Array.isArray(prop.items) ? prop.items : {});

const TextInput = ({ id, label, prop, value, disabled, onChange, placeholder }: ControlProps) => {
    const [text, setText] = React.useState(value === null || value === undefined ? '' : String(value));
    return (
        <TextField
            id={id}
            size="small"
            value={text}
            disabled={disabled}
            placeholder={placeholder || prop['x-format-hint'] || undefined}
            onChange={(ev) => {
                setText(ev.target.value);
                onChange(ev.target.value === '' ? null : ev.target.value);
            }}
            inputProps={{ 'aria-label': label, spellCheck: false, maxLength: prop.maxLength }}
            sx={{ width: { xs: '100%', md: 260 } }}
        />
    );
};

const SecretReplace = ({ id, label, value, disabled, onChange }: ControlProps) => {
    const [replacing, setReplacing] = React.useState(false);
    const secret = secretFieldProps(true);
    if (!replacing) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">{value ? 'Set' : 'Not set'}</Typography>
                <Button size="small" variant="outlined" disabled={disabled} onClick={() => setReplacing(true)}>Replace</Button>
            </Stack>
        );
    }
    return (
        <TextField
            id={id}
            size="small"
            type={secret.type}
            autoFocus
            autoComplete={secret.autoComplete}
            placeholder="New value"
            disabled={disabled}
            onChange={(ev) => onChange(ev.target.value === '' ? undefined : ev.target.value)}
            inputProps={{ ...secret.inputProps, 'aria-label': label }}
            sx={{ width: { xs: '100%', md: 220 } }}
        />
    );
};

const Control = (props: ControlProps) => {
    const { id, label, prop, value, disabled, onChange } = props;
    switch (widgetFor(prop)) {
        case 'boolean':
            return <Switch id={id} checked={!!value} disabled={disabled} onChange={(ev) => onChange(ev.target.checked)} inputProps={{ 'aria-label': label }} />;
        case 'enum': {
            const labels: string[] = Array.isArray(prop['x-enum-labels']) ? prop['x-enum-labels'] : [];
            const hints: string[] = Array.isArray(prop['x-enum-descriptions']) ? prop['x-enum-descriptions'] : [];
            const index = prop.enum.findIndex((v: any) => same(v, value));
            return (
                <TextField
                    id={id}
                    select
                    size="small"
                    value={index < 0 ? '' : String(index)}
                    disabled={disabled}
                    onChange={(ev) => onChange(prop.enum[Number(ev.target.value)])}
                    inputProps={{ 'aria-label': label }}
                    SelectProps={{ MenuProps: { sx: { zIndex: 10002 } } }}
                    sx={{ width: { xs: '100%', md: 260 } }}
                >
                    {prop.enum.map((v: any, i: number) => (
                        <MenuItem key={String(v)} value={String(i)} sx={{ whiteSpace: 'normal', maxWidth: 360, display: 'block' }}>
                            {labels[i] || String(v)}
                            {hints[i] && <Typography variant="caption" color="text.secondary" component="div">{hints[i]}</Typography>}
                        </MenuItem>
                    ))}
                </TextField>
            );
        }
        case 'number':
            return <NumberInput {...props} />;
        case 'list':
            return <ListInput {...props} />;
        case 'secret':
            return <SecretReplace {...props} />;
        case 'group':
            return <Group {...props} />;
        case 'readonly':
            return <Typography variant="body2">{settingText(value, prop)}</Typography>;
        default:
            return <TextInput {...props} />;
    }
};

/** A grouped object: its properties, each with its own control; a change replaces the whole object. */
const Group = ({ id, prop, root, value, disabled, onChange, onInvalid }: ControlProps) => {
    const current = value && typeof value === 'object' ? value : {};
    return (
        <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, width: '100%', display: 'grid', gap: 1.5 }}>
            {Object.entries(prop.properties || {}).map(([key, raw]) => {
                const child = resolve(root, raw as Schema);
                const childLabel = labelOf(key, child);
                return (
                    <Box key={key} sx={{ display: 'grid', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" component="label" htmlFor={`${id}-${key}`}>{childLabel}</Typography>
                        <Control
                            id={`${id}-${key}`}
                            label={childLabel}
                            prop={child}
                            root={root}
                            value={current[key]}
                            disabled={disabled}
                            onChange={(v) => onChange({ ...current, [key]: v })}
                            onInvalid={onInvalid}
                        />
                    </Box>
                );
            })}
        </Box>
    );
};

/** Where the value comes from, and the inherit / override / reset choice. */
const SourceLine = ({ level, stored, prop, pending, reset, inherited, parentName, canEdit, onReset, onUndo }: {
    level: Level;
    stored: SettingValue;
    prop: Schema;
    pending: boolean;
    reset: boolean;
    inherited?: SettingValue;
    parentName?: string | null;
    canEdit: boolean;
    onReset: () => void;
    onUndo: () => void;
}) => {
    const link = (text: string, onClick: () => void) => (
        <Button size="small" onClick={onClick} sx={{ minWidth: 0, p: 0, ml: 1, verticalAlign: 'baseline', typography: 'caption', fontWeight: 500, textTransform: 'none' }}>{text}</Button>
    );
    let text: string;
    let action: React.ReactNode = null;
    if (stored.mode === 'read') {
        text = stored.source === 'parent' ? `Set by ${parentName || 'the franchise'}` : 'Managed by Curated Planet';
    } else if (stored.source === 'parent') {
        text = `From ${parentName || 'the franchise'}`;
    } else if (reset) {
        text = level === 'account'
            ? `Inherits ${settingText(inherited?.value, prop)} from the connection once saved`
            : `Back to the default once saved${prop.default !== undefined ? `: ${settingText(prop.default, prop)}` : ''}`;
        action = link('Undo', onUndo);
    } else if (pending) {
        text = level === 'account' ? 'Overridden for this account once saved' : 'Changed, not saved yet';
        action = link('Undo', onUndo);
    } else if (level === 'account') {
        text = inheritText(stored, prop, inherited);
        if (stored.overridden && canEdit) {
            action = link('Reset', onReset);
        }
    } else if (stored.source === 'connection') {
        text = 'Changed from the default';
        if (canEdit) {
            action = link(prop.default !== undefined ? 'Use the default' : 'Clear', onReset);
        }
    } else {
        text = 'Default';
    }
    return (
        <Typography variant="caption" component="div" sx={{ mt: 0.25, color: pending || reset ? 'primary.main' : 'text.secondary' }} data-testid="setting-source">
            {text}
            {action}
        </Typography>
    );
};

/** The in-card settings form (plan §1.6), generated from the client view of the schema: `x-client: edit` keys
 *  get a control, `read` keys show their value. On a connection, a changed key can go back to the default; on
 *  an account, each key inherits the connection's value or overrides it (Reset inherits again). Save sends only
 *  what changed; a refusal changes nothing and names its keys. */
/** A cleared typed field shows emptiness, with what will apply as its placeholder; other controls show it. */
const TYPED: Widget[] = ['number', 'text'];

const SettingsEditor = ({ schema, values, level, editable, inherited, parentName, surface = 'background.paper', saveLabel = 'Save', onSave, onClose, onDirtyChange }: {
    schema: Schema;
    values: Record<string, SettingValue>;
    level: Level;
    editable: boolean;
    /** On an account: the connection's values, for what a reset inherits. */
    inherited?: Record<string, SettingValue>;
    parentName?: string | null;
    /** The colour under the sticky Save bar (it must be opaque): the card's, or the account panel's tint. */
    surface?: string;
    /** The Save button's words (the add flow keeps its choice for Add: "Use these settings"). */
    saveLabel?: string;
    onSave: (patch: Record<string, any>) => Promise<unknown>;
    onClose: () => void;
    /** Unsaved changes (typed-in invalid values included), so a Close elsewhere can ask before dropping them. */
    onDirtyChange?: (count: number) => void;
}) => {
    const [draft, setDraft] = React.useState<Record<string, any>>({});
    const [invalid, setInvalid] = React.useState<Set<string>>(new Set());
    // Bumped to remount a key's control (Undo, Reset) or every control (a save): fresh text from the value.
    const [mounts, setMounts] = React.useState<Record<string, number>>({});
    const [generation, setGeneration] = React.useState(0);
    const action = useAction();
    const properties: Record<string, Schema> = schema.properties || {};
    const keys = level === 'account' ? Object.keys(values) : Object.keys(properties).filter((k) => k in values);
    const errors = fieldErrors(action.error);
    const changes = Object.keys(draft).length;
    const dirty = new Set([...Object.keys(draft), ...Array.from(invalid)]).size;

    React.useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    /** The key holds its own value: on a connection a changed one, on an account an override. */
    const ownValue = (key: string) => (level === 'account' ? !!values[key]?.overridden : values[key]?.source === 'connection');

    const forget = (key: string, remount: boolean) => {
        setDraft((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setInvalid((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        if (remount) {
            setMounts((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
        }
    };
    const set = (key: string, value: any) => {
        // Clearing a field is a Reset where the key holds its own value, else no change at all.
        if (value === undefined || same(value, values[key]?.value) || (value === null && !ownValue(key))) {
            forget(key, false);
            return;
        }
        setDraft((prev) => ({ ...prev, [key]: value }));
    };
    const reset = (key: string) => {
        forget(key, true);
        setDraft((prev) => ({ ...prev, [key]: null }));
    };
    const markInvalid = (key: string, bad: boolean) => setInvalid((prev) => {
        const next = new Set(prev);
        if (bad) {
            next.add(key);
        } else {
            next.delete(key);
        }
        return next;
    });
    const cancel = () => {
        setDraft({});
        setInvalid(new Set());
        setGeneration((g) => g + 1);
        action.setError(null);
        onClose();
    };
    const save = async () => {
        const outcome = await action.run(() => onSave(draft));
        if (outcome.ok) {
            setDraft({});
            setInvalid(new Set());
            setGeneration((g) => g + 1);
            onClose();
        }
    };

    return (
        <Box data-testid={`settings-editor-${level}`}>
            {keys.map((key) => {
                const prop = resolve(schema, properties[key]);
                const stored = values[key];
                const label = labelOf(key, prop);
                const id = `setting-${level}-${key}`;
                const canEdit = editable && stored.mode === 'edit' && stored.source !== 'parent';
                const resetting = draft[key] === null && ownValue(key);
                const pending = (key in draft && !resetting) || invalid.has(key);
                // What applies without a value of its own: the connection's (account) or the default.
                const fallback = level === 'account' ? inherited?.[key]?.value : prop.default;
                const typed = TYPED.includes(widgetFor(prop));
                let shown = key in draft ? draft[key] : stored.value;
                if (resetting) {
                    shown = typed ? null : fallback;
                }
                const placeholder = typed && fallback !== undefined && fallback !== null ? settingText(fallback, prop) : undefined;
                const error = errors[key];
                return (
                    <Box
                        key={key}
                        data-testid={`setting-${key}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(220px, auto)' },
                            columnGap: 3,
                            rowGap: 1,
                            py: 1.5,
                            borderTop: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" component="label" htmlFor={id} sx={{ fontWeight: 500, display: 'block' }}>
                                {label}
                                {prop.deprecated && <Typography component="span" variant="caption" color="text.secondary"> (deprecated)</Typography>}
                            </Typography>
                            {prop.description && (
                                <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0, maxWidth: '65ch' }}>{prop.description}</Typography>
                            )}
                            <SourceLine
                                level={level}
                                stored={stored}
                                prop={prop}
                                pending={pending}
                                reset={resetting}
                                inherited={inherited?.[key]}
                                parentName={parentName}
                                canEdit={canEdit}
                                onReset={() => reset(key)}
                                onUndo={() => forget(key, true)}
                            />
                            {error && <Typography variant="caption" color="error" component="div" role="alert">{error}</Typography>}
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: { md: 'flex-end' } }}>
                            {canEdit ? (
                                <Control
                                    key={`${generation}-${mounts[key] || 0}`}
                                    id={id}
                                    label={label}
                                    prop={prop}
                                    root={schema}
                                    value={shown}
                                    placeholder={placeholder}
                                    disabled={action.busy}
                                    onChange={(v) => set(key, v)}
                                    onInvalid={(bad) => markInvalid(key, bad)}
                                />
                            ) : (
                                <Typography variant="body2" sx={{ overflowWrap: 'anywhere', textAlign: { md: 'right' } }}>{settingText(shown, prop)}</Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
            {editable && (
                <Box
                    data-testid="settings-save-bar"
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        bgcolor: surface,
                        borderTop: 1,
                        borderColor: 'divider',
                        pt: 1.5,
                        pb: 0.5,
                    }}
                >
                    {action.error ? <ErrorAlert error={action.error} /> : null}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={save}
                            disabled={!changes || invalid.size > 0 || action.busy}
                            startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                        >
                            {saveLabel}
                        </Button>
                        <Button size="small" onClick={cancel} disabled={action.busy}>Cancel</Button>
                        <Typography variant="caption" color="text.secondary" aria-live="polite">
                            {invalid.size > 0 ? 'Fix the marked values first.' : changes ? `${changes} unsaved ${changes === 1 ? 'change' : 'changes'}` : 'No changes yet'}
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default SettingsEditor;
